import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Search, ArrowRight, Star, Shield, Tag, ChevronRight,
  ExternalLink, Sparkles, Award, Grid3X3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const badgeStyle = (theme) => {
  const map = { blue: 'bg-blue-50 text-blue-700', amber: 'bg-amber-50 text-amber-700', violet: 'bg-violet-50 text-violet-700', emerald: 'bg-emerald-50 text-emerald-700', rose: 'bg-rose-50 text-rose-700', sky: 'bg-sky-50 text-sky-700' };
  return map[theme] || 'bg-gray-50 text-gray-600';
};

function PartnerCard({ partner, size = 'small' }) {
  const trackClick = () => {
    fetch(`${API}/partners/click/${partner.partner_id}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ source_page: 'partners' }) }).catch(() => {});
  };
  const isLarge = size === 'large' || size === 'featured';
  const isMedium = size === 'medium';
  return (
    <Card className={`border-gray-100 shadow-sm hover:shadow-lg transition-all group overflow-hidden ${isLarge ? 'row-span-2 col-span-2' : isMedium ? 'col-span-2' : ''}`} data-testid={`partner-card-${partner.slug}`}>
      <CardContent className={`${isLarge ? 'p-6' : 'p-4'} h-full flex flex-col`}>
        {partner.logo_url && <img src={partner.logo_url} alt={partner.name} className="h-10 w-auto object-contain mb-3" />}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className={`font-semibold text-[#0f1f33] group-hover:text-[#2563eb] transition-colors ${isLarge ? 'text-lg' : 'text-sm'}`}>{partner.name}</h3>
          {partner.is_sponsored && <Badge className="bg-violet-50 text-violet-700 text-[10px] shrink-0">Sponsored</Badge>}
        </div>
        <p className={`text-gray-500 leading-relaxed mb-3 flex-1 ${isLarge ? 'text-sm' : 'text-xs'} line-clamp-${isLarge ? '4' : '2'}`}>{partner.short_description}</p>
        {/* Badges */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {partner.badges?.map((b, i) => (
            <Badge key={i} className={`text-[10px] ${badgeStyle(b.color_theme)}`}>{b.label}</Badge>
          ))}
        </div>
        {/* Discount / Coupon */}
        {(partner.discount_text || partner.coupon_code) && (
          <div className={`rounded-lg p-2.5 mb-3 ${partner.is_exclusive_discount ? 'bg-rose-50 border border-rose-100' : 'bg-emerald-50 border border-emerald-100'}`}>
            {partner.discount_text && <p className="text-xs font-medium text-[#0f1f33]">{partner.discount_text}</p>}
            {partner.coupon_code && <p className="text-xs text-gray-500 mt-0.5">Code: <span className="font-mono font-semibold text-[#0f1f33]">{partner.coupon_code}</span></p>}
          </div>
        )}
        <div className="flex gap-2 mt-auto">
          <Link to={`/partners/${partner.slug}`} className="flex-1">
            <Button variant="outline" size="sm" className="w-full text-xs">Details</Button>
          </Link>
          {partner.affiliate_url && (
            <a href={partner.affiliate_url} target="_blank" rel="noopener noreferrer" onClick={trackClick} className="flex-1">
              <Button size="sm" className="w-full bg-[#c41230] hover:bg-[#a00f28] text-white text-xs" data-testid={`cta-${partner.slug}`}>
                {partner.primary_cta_text || 'Visit Partner'} <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function PartnersLanding() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [exclusive, setExclusive] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [disclosure, setDisclosure] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      fetch(`${API}/partners/categories`).then(r => r.json()),
      fetch(`${API}/partners/featured`).then(r => r.json()),
      fetch(`${API}/partners/recommended`).then(r => r.json()),
      fetch(`${API}/partners/exclusive`).then(r => r.json()),
      fetch(`${API}/partners/disclosure`).then(r => r.json()),
    ]).then(([cats, feat, rec, excl, disc]) => {
      setCategories(cats.categories || []);
      setFeatured(feat.partners || []);
      setRecommended(rec.partners || []);
      setExclusive(excl.partners || []);
      setDisclosure(disc.text || '');
    }).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/partners/search?q=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <div className="min-h-screen bg-[#fafbfc]" data-testid="partners-landing">
      {/* Hero */}
      <div className="bg-gradient-to-b from-[#0f1f33] to-[#1e3a5f] text-white py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="bg-white/10 text-white/90 mb-4">Trusted Partner Directory</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">Member Benefits, Preferred Providers,<br className="hidden sm:block" /> and Exclusive Offers</h1>
          <p className="text-white/70 text-base sm:text-lg mb-8 max-w-2xl mx-auto">Tools, services, and solutions recommended for real estate professionals. Every partner is vetted and curated by AREA.</p>
          <form onSubmit={handleSearch} className="max-w-xl mx-auto flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search partners, services, offers..." className="pl-10 bg-white text-[#0f1f33] border-0 h-11" data-testid="partner-search" />
            </div>
            <Button type="submit" className="bg-[#c41230] hover:bg-[#a00f28] text-white h-11 px-6">Search</Button>
          </form>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12 space-y-16">
        {/* Featured Partners */}
        {featured.length > 0 && (
          <section data-testid="featured-section">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-bold text-[#0f1f33]">Featured Partners</h2>
              </div>
              <Link to="/partners/search?featured=true"><Button variant="ghost" size="sm" className="text-xs text-gray-400">View All <ChevronRight className="w-3 h-3 ml-1" /></Button></Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featured.map((p, i) => <PartnerCard key={i} partner={p} size={i === 0 ? 'medium' : 'small'} />)}
            </div>
          </section>
        )}

        {/* Browse by Category */}
        {categories.length > 0 && (
          <section data-testid="categories-section">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Grid3X3 className="w-5 h-5 text-[#2563eb]" />
                <h2 className="text-lg font-bold text-[#0f1f33]">Browse by Category</h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {categories.slice(0, 8).map((cat, i) => (
                <Link key={i} to={`/partners/category/${cat.slug}`} data-testid={`cat-card-${cat.slug}`}>
                  <div className="p-4 bg-white rounded-xl border border-gray-100 hover:border-[#2563eb]/30 hover:shadow-md transition-all group">
                    <h3 className="text-sm font-semibold text-[#0f1f33] group-hover:text-[#2563eb] mb-1">{cat.name}</h3>
                    <p className="text-xs text-gray-400 line-clamp-2 mb-2">{cat.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">{cat.partner_count} partner{cat.partner_count !== 1 ? 's' : ''}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#2563eb] transition-colors" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            {categories.length > 8 && (
              <div className="text-center mt-4">
                <Link to="/partners/search"><Button variant="ghost" size="sm" className="text-xs text-gray-400">View All Categories <ChevronRight className="w-3 h-3 ml-1" /></Button></Link>
              </div>
            )}
          </section>
        )}

        {/* Exclusive Member Benefits */}
        {exclusive.length > 0 && (
          <section data-testid="exclusive-section">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-rose-500" />
                <h2 className="text-lg font-bold text-[#0f1f33]">Exclusive Member Benefits</h2>
              </div>
              <Link to="/partners/exclusive"><Button variant="ghost" size="sm" className="text-xs text-gray-400">View All <ChevronRight className="w-3 h-3 ml-1" /></Button></Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {exclusive.map((p, i) => <PartnerCard key={i} partner={p} />)}
            </div>
          </section>
        )}

        {/* Recommended */}
        {recommended.length > 0 && (
          <section data-testid="recommended-section">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-sky-500" />
                <h2 className="text-lg font-bold text-[#0f1f33]">Recommended for AREA Members</h2>
              </div>
              <Link to="/partners/recommended"><Button variant="ghost" size="sm" className="text-xs text-gray-400">View All <ChevronRight className="w-3 h-3 ml-1" /></Button></Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recommended.map((p, i) => <PartnerCard key={i} partner={p} />)}
            </div>
          </section>
        )}

        {/* Trust Section */}
        <section className="bg-white rounded-2xl border border-gray-100 p-8">
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-3"><Shield className="w-6 h-6 text-blue-600" /></div>
              <h3 className="text-sm font-semibold text-[#0f1f33] mb-1">Vetted Partners</h3>
              <p className="text-xs text-gray-400">Every partner is reviewed and approved by the AREA team.</p>
            </div>
            <div>
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mx-auto mb-3"><Tag className="w-6 h-6 text-emerald-600" /></div>
              <h3 className="text-sm font-semibold text-[#0f1f33] mb-1">Member Benefits</h3>
              <p className="text-xs text-gray-400">Exclusive discounts and offers available only to AREA members.</p>
            </div>
            <div>
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mx-auto mb-3"><Award className="w-6 h-6 text-amber-600" /></div>
              <h3 className="text-sm font-semibold text-[#0f1f33] mb-1">Curated Recommendations</h3>
              <p className="text-xs text-gray-400">Hand-picked tools and services for real estate professionals.</p>
            </div>
          </div>
        </section>

        {/* Disclosure */}
        {disclosure && (
          <div className="text-center">
            <p className="text-xs text-gray-400 max-w-2xl mx-auto italic">{disclosure}</p>
          </div>
        )}
      </div>
    </div>
  );
}
